import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import FormValidation from "../../../Common/FormValidation";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import { LoaderComponent } from "../../../Common/LoaderComponent";
import { Calendar } from "primereact/calendar";
import { ProgressSpinner } from "primereact/progressspinner";

const ExpenseEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [inputs, setInputs] = useState({});
  const [error, setError] = useState({});
  const [categories, setCategories] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bankBalance, setBankBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [balanceError, setBalanceError] = useState("");

  useEffect(() => {
    Promise.all([
      Http.get(`/accounts/expenses/${id}`),
      Http.get("/accounts/expense-categories"),
      Http.get("/accounts/banks")
    ])
      .then(([expenseRes, categoriesRes, banksRes]) => {
        const expenseData = expenseRes.data.data;
        if (expenseData.date) {
          expenseData.date = new Date(expenseData.date);
        }
        setInputs(expenseData);
        setCategories(categoriesRes.data);
        setBanks(banksRes.data);
        // Fetch bank balance for the current bank
        if (expenseData.bank_id) {
          fetchBankBalance(expenseData.bank_id);
        }
      })
      .catch((error) => {
        console.error("Fetch error:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  const fetchBankBalance = (bankId) => {
    if (!bankId) {
      setBankBalance(null);
      setBalanceError("");
      return;
    }

    setLoadingBalance(true);
    setBalanceError("");
    Http.get(`/accounts/get-single-bank-ledger-summary/${bankId}`)
      .then((res) => {
        if (res.data.success) {
          setBankBalance(res.data.data);
        } else {
          setBalanceError("Failed to fetch bank balance");
        }
      })
      .catch((error) => {
        console.error("Balance fetch error:", error);
        setBalanceError("Error fetching bank balance");
        setBankBalance(null);
      })
      .finally(() => {
        setLoadingBalance(false);
      });
  };

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setInputs((values) => ({ ...values, [name]: value }));

    // Fetch bank balance when bank is selected
    if (name === "bank_id") {
      fetchBankBalance(value);
    }

    // Balance check on amount/bank change
    if (name === "amount" || name === "bank_id") {
      setTimeout(() => {
        const amount = parseFloat(name === "amount" ? value : inputs.amount) || 0;
        const hasBank = name === "bank_id" ? !!value : !!inputs.bank_id;
        const rawBalance = bankBalance?.total_balance;
        const balance = rawBalance !== undefined && rawBalance !== null
          ? parseFloat(String(rawBalance).replace(/[^0-9.-]/g, ""))
          : null;
        if (hasBank && amount > 0 && balance !== null && !isNaN(balance) && amount > balance) {
          setBalanceError("Expense amount exceeds current bank balance!");
        } else {
          setBalanceError("");
        }
      }, 0);
    }
  };

  const handleDateChange = (e) => {
    console.log(e.value);
    setInputs((values) => ({ ...values, date: e.value }));
  };

  const formSubmit = () => {
    const amount = parseFloat(inputs.amount || 0) || 0;

    const rawBalance = bankBalance?.total_balance;
    const balance =
      rawBalance !== undefined && rawBalance !== null
        ? parseFloat(String(rawBalance).replace(/[^0-9.-]/g, ""))
        : null;

    if (
      inputs.bank_id &&
      amount > 0 &&
      balance !== null &&
      !isNaN(balance) &&
      amount > balance
    ) {
      setBalanceError("Expense amount exceeds current bank balance!");
      setError({
        general: ["Please correct the highlighted amount before submitting."]
      });
      return;
    }

    setBalanceError("");
    setError({});

    const payload = {
      ...inputs,
      date: inputs.date
        ? `${inputs.date.getFullYear()}-${String(
          inputs.date.getMonth() + 1
        ).padStart(2, "0")}-${String(
          inputs.date.getDate()
        ).padStart(2, "0")}`
        : null,
    };
    Http.put(`/accounts/expenses/${id}`, payload)
      .then((res) => {
        navigate("/expenses", {
          state: {
            message: res.data.message,
            type: "success",
          },
        });
      })
      .catch((e) => {
        setError(
          e.response?.data?.errors || {
            general: ["An error occurred"],
          }
        );
      });
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <LoaderComponent />
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Edit Expense</h5>
        </div>
        <div className="am-pagebody">
          <div className="card pd-20 pd-sm-40">
            <h6 className="card-body-title mg-sm-b-30">Expense Information</h6>
            <div className="row">
              <div className="col-lg">
                <FormValidation error={error} />
              </div>
            </div>

            {loading ? (
              <ProgressSpinner />
            ) : (
              <>
                <div className="row">
                  <div className="col-lg">
                    <label>Date <span className="text-danger">*</span></label>
                    <Calendar
                      value={inputs.date}
                      onChange={handleDateChange}
                      dateFormat="dd/mm/yy"
                      className="w-100"
                    />
                  </div>
                  <div className="col-lg">
                    <label>Category <span className="text-danger">*</span></label>
                    <select
                      className="form-control"
                      name="expense_category_id"
                      value={inputs.expense_category_id || ""}
                      onChange={handleChange}
                    >
                      <option value="">Select Category</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-lg">
                    <label>Amount <span className="text-danger">*</span></label>
                    <input
                      type="number"
                      className="form-control"
                      name="amount"
                      value={inputs.amount || ""}
                      onChange={handleChange}
                      placeholder="Enter Amount"
                    />
                    {balanceError && (
                      <div className="text-danger mt-1">
                        <i className="fa fa-exclamation-circle me-1"></i>
                        {balanceError}
                      </div>
                    )}
                  </div>
                  <div className="col-lg">
                    <label>Bank <span className="text-danger">*</span></label>
                    <select
                      className="form-control"
                      name="bank_id"
                      value={inputs.bank_id || ""}
                      onChange={handleChange}
                    >
                      <option value="">Select Bank</option>
                      {banks.map((bank) => (
                        <option key={bank.id} value={bank.id}>
                          {bank.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Bank Balance Display */}
                {inputs.bank_id && (
                  <div className="row mt-2">
                    <div className="col-lg-6">
                      <div className="alert alert-info" style={{ marginBottom: '15px' }}>
                        <strong>Bank Balance Information:</strong>
                        {loadingBalance ? (
                          <div className="d-flex align-items-center mt-2">
                            <div className="spinner-border spinner-border-sm me-2" role="status">
                              <span className="sr-only">Loading...</span>
                            </div>
                            <span>Loading bank balance...</span>
                          </div>
                        ) : balanceError ? (
                          <div className="text-danger mt-2">
                            <i className="fa fa-exclamation-triangle me-1"></i>
                            {balanceError}
                          </div>
                        ) : bankBalance ? (
                          <div className="mt-2">
                            <div className="row">
                              <div className="col-md-4">
                                <small className="text-muted">Bank Name:</small>
                                <div className="font-weight-bold">{bankBalance.name}</div>
                              </div>
                              <div className="col-md-4">
                                <small className="text-muted">Account Number:</small>
                                <div className="font-weight-bold">{bankBalance.account_number}</div>
                              </div>
                              <div className="col-md-4">
                                <small className="text-muted">Current Balance:</small>
                                <div className="font-weight-bold text-success">
                                  ৳ {bankBalance.total_balance}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                )}

                <div className="row mt-3">
                  <div className="col-lg">
                    <label>Reference</label>
                    <input
                      type="text"
                      className="form-control"
                      name="reference"
                      value={inputs.reference || ""}
                      onChange={handleChange}
                      placeholder="Enter Reference"
                    />
                  </div>

                  <div className="row mt-3">
                    <div className="col-lg">
                      <label>Description</label>
                      <textarea
                        className="form-control"
                        name="description"
                        value={inputs.description || ""}
                        onChange={handleChange}
                        placeholder="Enter Description"
                        rows="3"
                      ></textarea>
                    </div>
                    {/* <div className="col-lg">
                    <label>Status</label>
                    <select
                      className="form-control"
                      name="status"
                      value={inputs.status || ""}
                      onChange={handleChange}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div> */}
                  </div>

                  <div className="row mt-3">
                    <div className="col-3">
                      <button
                        className="btn btn-primary btn-block mg-b-10"
                        onClick={formSubmit}
                      >
                        <i className="fa fa-send mg-r-10"></i> Update
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseEdit;